import { ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import wordLogo from "@/assets/microsoft-word-logo.svg";
import excelLogo from "@/assets/microsoft-excel-logo.svg";
import { useState } from "react";

export type ExportFormat = "docx" | "xlsx";

interface FormatSelectorProps {
  supportedFormats: string[];
  selectedFormat: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  disabled?: boolean;
  pillClassName?: string;
}

const FORMAT_CONFIG = {
  docx: {
    logo: wordLogo,
    label: "Word",
    tooltip: "Exports to Microsoft Word (.docx)",
    iconClass: "h-6 w-6 object-contain",
  },
  xlsx: {
    logo: excelLogo,
    label: "Excel",
    tooltip: "Exports to Microsoft Excel (.xlsx)",
    iconClass: "h-6 w-6 object-contain",
  },
} as const;

export function FormatSelector({
  supportedFormats,
  selectedFormat,
  onFormatChange,
  disabled = false,
  pillClassName,
}: FormatSelectorProps) {
  const [open, setOpen] = useState(false);
  
  // Filter to only supported formats that we have config for
  const availableFormats = supportedFormats.filter(
    (f): f is ExportFormat => f === "docx" || f === "xlsx"
  );

  const currentConfig = FORMAT_CONFIG[selectedFormat];

  // If only one format available, show static pill
  if (availableFormats.length <= 1) {
    const format = availableFormats[0] || "docx";
    const config = FORMAT_CONFIG[format];

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn("flex items-center gap-1.5 h-9 px-3 shrink-0 rounded-full transition-colors", pillClassName || "bg-muted/50 hover:bg-muted")}>
              <div className="w-6 flex items-center justify-center shrink-0"><img src={config.logo} alt={config.label} className={config.iconClass} /></div>
              <span className="text-sm">{config.label}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{config.tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Multiple formats - show dropdown
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          className={cn(
            "flex items-center gap-1.5 h-9 px-3 shrink-0 rounded-full transition-colors",
            pillClassName || "bg-muted/50 hover:bg-muted",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          <div className="w-6 flex items-center justify-center shrink-0"><img src={currentConfig.logo} alt={currentConfig.label} className={currentConfig.iconClass} /></div>
          <span className="text-sm">{currentConfig.label}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto min-w-[120px] p-1 bg-background border shadow-lg z-50" 
        align="start"
        sideOffset={4}
      >
        <div className="flex flex-col gap-0.5">
          {availableFormats.map((format) => {
            const config = FORMAT_CONFIG[format];
            const isSelected = selectedFormat === format;

            return (
              <button
                key={format}
                onClick={() => {
                  onFormatChange(format);
                  setOpen(false);
                }}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-md transition-colors text-left w-full",
                  isSelected
                    ? "bg-muted font-medium"
                    : "hover:bg-muted/50"
                )}
              >
                <div className="w-6 flex items-center justify-center shrink-0">
                  <img
                    src={config.logo}
                    alt={config.label}
                    className={config.iconClass}
                  />
                </div>
                <span className="text-sm">{config.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
